// Purpose: isomorphic (browser + server safe) catalog of AST legal notices.
//
// EDITED BY APOGEE LEGAL / ISSO. This file is the authoritative source of
// the consent-to-monitor, privacy, and terms copy. Its git history IS the
// version record. When you change a notice body:
//   1. Update its `version` (date the change lands).
//   2. Recompute `hash` — run `pnpm --filter @ast/legal-notices test`; the
//      drift test fails and prints the expected hash. Paste it in.
// The hash is what the audit trail records, so it must match the content.
//
// No @prisma/client, no node-only APIs, no runtime dependencies: this
// module ships to the browser AND runs on the server.

export type NoticeBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

export type NoticeId = 'system-use' | 'privacy' | 'terms';

export interface LegalNotice {
  id: NoticeId;
  version: string;
  hash: string;
  title: string;
  blocks: NoticeBlock[];
}

export const SYSTEM_USE_NOTICE: LegalNotice = {
  id: 'system-use',
  version: '2026-07-13',
  hash: 'fd795bd8226b25d2654dde3987ce692a4bd72bf2ddfd005af3501306bb4bd673',
  title: 'System Use Notice',
  blocks: [
    {
      kind: 'paragraph',
      text: 'You are accessing the Apogee Secure Transfer (AST) information system, provided for authorized Apogee Engineering, LLC business use only.',
    },
    {
      kind: 'paragraph',
      text: 'By continuing, you consent to the following: Your use of this system may be monitored, recorded, and audited. Activity on this system, including IP address, browser, and actions taken, is logged in a tamper-evident audit record. You have no expectation of privacy in your use of this system.',
    },
    {
      kind: 'paragraph',
      text: 'Unauthorized use, or use for any purpose other than authorized Apogee business, is prohibited and may result in disciplinary action and civil or criminal penalties.',
    },
    {
      kind: 'paragraph',
      text: 'This system may hold Controlled Unclassified Information (CUI). Handle all information in accordance with applicable Apogee policy and your CUI obligations.',
    },
  ],
};

export const PRIVACY_NOTICE: LegalNotice = {
  id: 'privacy',
  version: '2026-07-13',
  hash: 'b530fd9d2a2b776c26c6866cf93275178429312a288f3a78582b55aff44764fb',
  title: 'Privacy Notice',
  blocks: [
    { kind: 'heading', text: 'What we log' },
    {
      kind: 'paragraph',
      text: 'AST records information necessary to secure the system and maintain an audit trail. This includes your account identity, IP address, browser user-agent, and the actions you take (sign-in, share and dropbox activity, downloads, administrative actions).',
    },
    { kind: 'heading', text: 'Why we log it' },
    {
      kind: 'paragraph',
      text: 'This information is used for security, integrity, troubleshooting, and compliance with Apogee obligations, including CUI handling requirements. It is retained in a tamper-evident audit record.',
    },
    { kind: 'heading', text: 'External recipients and uploaders' },
    {
      kind: 'paragraph',
      text: 'If you receive or upload files through an AST link without an Apogee account, AST records the email address used, your IP address, browser user-agent, and the file activity, for the same security and audit purposes.',
    },
    { kind: 'heading', text: 'Employee data' },
    {
      kind: 'paragraph',
      text: 'Where AST processes personal information of Apogee personnel, it is handled under Apogee employee privacy practices. Direct questions to Apogee HR or the AST administrators.',
    },
  ],
};

export const TERMS_OF_USE: LegalNotice = {
  id: 'terms',
  version: '2026-07-13',
  hash: 'ba4684ea2beaa7a6c6f2125e4b7c17297916fb3b85e9988ee09325723dda5bca',
  title: 'Terms of Use',
  blocks: [
    {
      kind: 'paragraph',
      text: 'AST is provided for authorized file transfer. Do not use it for any unlawful purpose or to store or transmit material you are not authorized to handle.',
    },
    {
      kind: 'list',
      items: [
        'Use of this system is monitored and recorded.',
        'AST is provided without warranty of any kind.',
        'Do not attempt to bypass access controls or share access credentials or links beyond their intended recipients.',
        'Report suspected misuse or security issues to the AST administrators.',
      ],
    },
  ],
};

export const ALL_NOTICES: readonly LegalNotice[] = [
  SYSTEM_USE_NOTICE,
  PRIVACY_NOTICE,
  TERMS_OF_USE,
];
